import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_TYPES } from "@/data/site";
import { CheckCircle2 } from "lucide-react";

export default function Quote() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    service_type: "", project_description: "", timeline: "",
  });
  const on = (k) => (e) => setForm({ ...form, [k]: typeof e === "string" ? e : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type) { toast.error("Please choose a service type"); return; }
    setSubmitting(true);
    try {
      await api.post("/quotes", { ...form, source_page: location.pathname });
      setSubmitted(true);
      toast.success("Quote request received.", { duration: 4000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="quote-success" className="section-y">
        <div className="container-x max-w-2xl">
          <CheckCircle2 size={56} className="text-accent" />
          <h1 className="font-serif text-5xl md:text-6xl text-primary mt-6 leading-[0.95]">Thank you.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Your request is in our queue. A member of our workshop team will respond shortly — usually within one business day.
          </p>
          <div className="mt-10 flex gap-4 flex-wrap">
            <Button onClick={() => navigate("/")} className="rounded-none uppercase tracking-[0.18em] text-xs px-6 py-3.5 h-auto">Back to home</Button>
            <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ name:"", company:"", email:"", phone:"", service_type:"", project_description:"", timeline:"" }); }} className="rounded-none uppercase tracking-[0.18em] text-xs px-6 py-3.5 h-auto">Submit another</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="section-y">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">Request a Quote</p>
          <h1 className="font-serif text-5xl md:text-6xl text-primary mt-4 leading-[0.95]">
            Tell us about the <span className="italic text-secondary">job</span>.
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-md">
            Share the basics — service type, description and timeline — and we&apos;ll come back with pricing and lead time.
            For urgent breakdowns, please also call us directly.
          </p>
        </div>

        <div className="lg:col-span-7 bg-white border border-border p-6 md:p-10">
          <form onSubmit={submit} className="space-y-6" data-testid="quote-request-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="q-name">Full Name *</Label>
                <Input id="q-name" data-testid="quote-name" required value={form.name} onChange={on("name")} className="mt-2 rounded-none" />
              </div>
              <div>
                <Label htmlFor="q-company">Company</Label>
                <Input id="q-company" data-testid="quote-company" value={form.company} onChange={on("company")} className="mt-2 rounded-none" />
              </div>
              <div>
                <Label htmlFor="q-email">Email *</Label>
                <Input id="q-email" type="email" data-testid="quote-email" required value={form.email} onChange={on("email")} className="mt-2 rounded-none" />
              </div>
              <div>
                <Label htmlFor="q-phone">Phone *</Label>
                <Input id="q-phone" data-testid="quote-phone" required value={form.phone} onChange={on("phone")} className="mt-2 rounded-none" />
              </div>
            </div>

            <div>
              <Label>Service Type *</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="quote-service" className="mt-2 rounded-none">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="q-desc">Project Description *</Label>
              <Textarea id="q-desc" required rows={6} data-testid="quote-description" value={form.project_description} onChange={on("project_description")} className="mt-2 rounded-none" placeholder="Material, dimensions, quantity, drawings available, etc." />
            </div>

            <div>
              <Label htmlFor="q-time">Timeline</Label>
              <Input id="q-time" data-testid="quote-timeline" value={form.timeline} onChange={on("timeline")} placeholder="e.g. ASAP / Within 2 weeks / End of month" className="mt-2 rounded-none" />
            </div>

            <Button type="submit" disabled={submitting} data-testid="quote-submit-btn" className="rounded-none uppercase tracking-[0.18em] text-xs px-6 py-3.5 h-auto">
              {submitting ? "Submitting…" : "Submit Request"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
